import {
  ArrowLeft,
  Bell,
  Briefcase,
  MessageSquare,
  ShieldCheck,
  User,
  Zap,
  Star,
  CheckCircle2,
  Play,
  Phone,
  Mail,
} from "lucide-react";

const FreshmentAppPage = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      {/* Navigation Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1 rounded-lg">
              <Briefcase className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg">Freshment App</span>
          </div>
          <a
            className="flex xsm:hidden bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-slate-800 transition"
            href="https://play.google.com/store/apps/details?id=com.govtjobs.naukari"
            target="_blank"
            rel="noreferrer"
          >
            Download Now
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8">
          <div className="flex flex-row sm:flex-col items-center gap-16">
            <div className="w-1/2 xl:w-full text-left xl:text-center z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-bold uppercase tracking-widest mb-6">
                <Star className="w-3.5 h-3.5 fill-current" />
                Rated 4.8/5 on Play Store
              </div>
              <h1 className="text-6xl xl:text-5xl font-black text-slate-900 mb-6 leading-[1.1]">
                Your Dream Job, <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Now Pocket-Sized.
                </span>
              </h1>
              <p className="text-xl text-slate-500 mb-10 leading-relaxed max-w-lg mx-0 xl:mx-auto">
                Experience the fastest way to get hired. Real-time alerts,
                direct HR contact, and a resume builder—all in one powerful app.
              </p>

              <div className="flex flex-row justify-start sm:justify-center gap-4">
                <a
                  href="https://play.google.com/store/apps/details?id=com.govtjobs.naukari"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 w-fit bg-slate-900 text-white px-6 py-3.5 rounded-xl hover:bg-slate-800 transition shadow-xl hover:-translate-y-1"
                >
                  <Play className="w-8 h-8 fill-current" />
                  <div className="text-left">
                    <div className="text-xs uppercase font-bold text-gray-400 leading-none mb-1">
                      Get it on
                    </div>
                    <div className="text-xl font-bold leading-none">
                      Google Play
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div className="w-1/2 xl:w-full flex justify-center relative">
              {/* Decorative Blobs */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl -z-10"></div>

              {/* Main Phone Mockup */}
              <div className="relative w-[320px] h-[640px] bg-slate-900 rounded-[3rem] border-8 border-slate-800 shadow-2xl overflow-hidden z-10">
                <div className="absolute inset-0 bg-white overflow-hidden flex flex-col">
                  {/* Fake App Header */}
                  <div className="h-24 bg-blue-600 p-6 flex items-end justify-between">
                    <div className="text-white font-bold text-lg">
                      Freshment
                    </div>
                    <Bell className="w-5 h-5 text-white/80" />
                  </div>
                  {/* Content */}
                  <div className="p-4 space-y-4 bg-slate-50 flex-1">
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                      <div className="flex justify-between items-start mb-2">
                        <div className="w-10 h-10 bg-indigo-50 rounded-xl"></div>
                        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded">
                          NEW
                        </span>
                      </div>
                      <div className="h-4 w-3/4 bg-slate-200 rounded mb-2"></div>
                      <div className="h-3 w-1/2 bg-slate-100 rounded"></div>
                      <div className="mt-4 flex gap-2">
                        <div className="h-8 flex-1 bg-slate-900 rounded-lg"></div>
                        <div className="h-8 w-8 bg-slate-100 rounded-lg"></div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 opacity-60">
                      <div className="flex justify-between items-start mb-2">
                        <div className="w-10 h-10 bg-orange-50 rounded-xl"></div>
                      </div>
                      <div className="h-4 w-3/4 bg-slate-200 rounded mb-2"></div>
                      <div className="h-3 w-1/2 bg-slate-100 rounded"></div>
                    </div>
                    {/* Floating Action Button */}
                    <div className="absolute bottom-6 right-6 w-14 h-14 bg-blue-600 rounded-full shadow-lg flex items-center justify-center text-white">
                      <MessageSquare className="w-6 h-6" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Freshment Works - Mobile Focus */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black text-slate-900 mb-4">
              How Freshment Works
            </h2>
            <p className="text-lg text-slate-500">
              Simplify your job search in 4 easy steps
            </p>
          </div>

          <div className="grid grid-cols-4 xsm:grid-cols-1 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="block sm:hidden absolute top-12 left-0 right-0 h-0.5 bg-slate-100 -z-10"></div>

            {[
              {
                icon: User,
                title: "Create Profile",
                desc: "Sign up and build your digital resume in minutes.",
              },
              {
                icon: Bell,
                title: "Get Alerts",
                desc: "Receive instant notifications for jobs matching your skills.",
              },
              {
                icon: Zap,
                title: "Apply Instantly",
                desc: "One-tap apply to multiple companies without repetitive forms.",
              },
              {
                icon: Phone,
                title: "Contact HR",
                desc: "Directly communicate with recruiters via phone or email.",
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow text-center group"
              >
                <div className="w-16 h-16 mx-auto bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform relative z-10 border-4 border-white shadow-sm">
                  <step.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid & Screenshots */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 xl:px-6 sm:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl xl:text-3xl font-black text-slate-900 mb-4">
              Everything You Need to Get Hired
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              We've packed the Freshment app with powerful features designed
              specifically for freshers and early-career professionals.
            </p>
          </div>

          <div className="grid grid-cols-3 xsm:grid-cols-1 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Real-time Job Alerts
              </h3>
              <p className="text-slate-500 mb-8">
                Never miss an opportunity. Get notified the second a relevant
                job is posted.
              </p>
              {/* Mini Mockup */}
              <div className="w-48 bg-slate-100 rounded-t-2xl p-3 pb-0 border border-slate-200 mt-auto">
                <div className="bg-white rounded-t-lg p-3 shadow-sm">
                  <div className="flex gap-2 items-center mb-2">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full"></div>
                    <div className="h-2 w-20 bg-slate-100 rounded"></div>
                  </div>
                  <div className="h-2 w-full bg-slate-50 rounded"></div>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                100% Verified Jobs
              </h3>
              <p className="text-slate-500 mb-8">
                Safe job search. Every company and job posting is manually
                verified by our team.
              </p>
              <div className="w-48 bg-slate-100 rounded-t-2xl p-3 pb-0 border border-slate-200 mt-auto flex justify-center items-end">
                <div className="bg-white rounded-t-xl p-4 shadow-sm border border-slate-100 mb-2">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
              </div>
            </div>

            {/* Feature 3 - UPDATED */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Phone className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Direct HR Contact
              </h3>
              <p className="text-slate-500 mb-8">
                Skip the emails. Chat directly with recruiters to ask questions
                and schedule interviews.
              </p>
              <div className="w-48 bg-slate-100 rounded-t-2xl p-3 pb-0 border border-slate-200 mt-auto flex flex-col gap-2">
                <div className="bg-white p-2 rounded-lg shadow-sm flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="w-3 h-3 text-green-600" />
                  </div>
                  <div className="h-2 w-20 bg-slate-100 rounded"></div>
                </div>
                <div className="bg-white p-2 rounded-lg shadow-sm flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="h-2 w-24 bg-slate-100 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="py-20 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-black mb-6">
            Ready to launch your career?
          </h2>
          <p className="text-slate-400 text-lg mb-10">
            Join 2,00,000+ freshers who trust Freshment for their job search.
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.govtjobs.naukari"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 w-fit bg-white text-slate-900 px-6 py-3 rounded-xl font-bold hover:bg-slate-100 transition shadow-xl  mx-auto"
          >
            <Play className="w-6 h-6 fill-current" />
            <div className="text-left">
              <div className="text-[10px] uppercase font-bold text-slate-500 leading-none mb-0.5">
                Get it on
              </div>
              <div className="text-sm font-bold leading-none">Google Play</div>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
};

export default FreshmentAppPage;
